import { Icon } from '@iconify/react';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { Route } from 'nextjs-routes';
import { type FC, useEffect, useRef, useState } from 'react';

import LogoWhite from '@/assets/logo-mindpal.svg';
import LogoPrimary from '@/assets/logo-primary.svg';
import NavigationButton from '@/components/NavigationButton';
import { useHiddenHeader } from '@/hooks/useHiddenHeader';

import type { NavGroup, NavItem } from '../navigation';
import { useNavigation } from '../useNavigation';

/* ---- exact values from mindpal.co nav.css ---- */
const ACCENT = '#895bfe';
const APP_LINK = '#d0bdff';
const DIVIDER = '#383838';
const TEXT_DIM = '#d5d6d8';

type Theme = 'dark' | 'light';

const isItemActive = (item: NavItem, pathname: string) => {
  if (item.href)
    return pathname === item.href || pathname.startsWith(`${item.href}/`);
  return (
    item.rows
      ?.flat()
      .flatMap((g) => g.links)
      .some(
        (l) =>
          !l.contact &&
          l.href !== '#' &&
          (pathname === l.href || pathname.startsWith(`${l.href}/`))
      ) ?? false
  );
};

const NavLinkItem: FC<{ link: NavGroup['links'][number]; theme: Theme }> = ({
  link,
  theme,
}) => {
  const { pathname } = useRouter();
  const isActive =
    link.href !== '#' &&
    !link.contact &&
    (pathname === link.href || pathname.startsWith(`${link.href}/`));

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    font: '400 16px/24px Inter, sans-serif',
    transition: 'color .2s',
  };

  if (link.href === '#' && !link.contact) {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a
        href="#"
        onClick={(e) => e.preventDefault()}
        style={{
          ...baseStyle,
          color: theme === 'dark' ? 'rgba(255,255,255,0.9)' : '#1a1a1a',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.color =
            theme === 'dark' ? APP_LINK : '#603db5';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.color =
            theme === 'dark' ? 'rgba(255,255,255,0.9)' : '#1a1a1a';
        }}
      >
        {link.label}
      </a>
    );
  }

  if (link.external) {
    return (
      <Link
        href={link.href as unknown as Route}
        style={{
          ...baseStyle,
          color: APP_LINK,
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        }}
      >
        {link.label}
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          style={{ width: 14, height: 14 }}
        >
          <path
            fill="currentColor"
            d="m7.825 13l5.6 5.6L12 20l-8-8l8-8l1.425 1.4l-5.6 5.6H20v2z"
            transform="rotate(135 12 12)"
          />
        </svg>
      </Link>
    );
  }

  const href = (link.contact
    ? '/?contact=true'
    : link.href) as unknown as Route;
  // eslint-disable-next-line no-nested-ternary
  const color = isActive
    ? ACCENT
    : theme === 'dark'
    ? 'rgba(255,255,255,0.9)'
    : '#1a1a1a';

  return (
    <Link
      href={href}
      style={{ ...baseStyle, color }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.color =
          theme === 'dark' ? APP_LINK : '#603db5';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.color = color;
      }}
    >
      {link.label}
    </Link>
  );
};

const NavPanel: FC<{
  item: NavItem;
  theme: Theme;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}> = ({ item, theme, onMouseEnter, onMouseLeave }) => {
  const isDark = theme === 'dark';
  const panelBg = isDark ? '#000' : '#fff';
  const panelBorder = isDark ? 'none' : '1px solid #e3e3e3';
  const dividerColor = isDark ? DIVIDER : '#e3e3e3';

  return (
    <div
      className="nav-panel-animate"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'absolute',
        top: '100%',
        left: '40px',
        right: '40px',
        maxWidth: '1440px',
        marginInline: 'auto',
        display: 'flex',
        gap: '24px',
        padding: '40px',
        borderRadius: '16px',
        background: panelBg,
        border: panelBorder,
        zIndex: 50,
      }}
    >
      {/* side label */}
      <p
        style={{
          flexShrink: 0,
          width: '233px',
          margin: 0,
          font: '400 20px/28px Inter, sans-serif',
          color: isDark ? ACCENT : '#603db5',
        }}
      >
        {item.label}
      </p>

      {/* rows */}
      <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        {(item.rows ?? []).map((row, rowIndex) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={rowIndex}
            style={{
              display: 'flex',
              gap: '24px',
              ...(rowIndex > 0
                ? {
                    marginTop: '24px',
                    paddingTop: '24px',
                    borderTop: `1px solid ${dividerColor}`,
                  }
                : {}),
            }}
          >
            {row.map((group, groupIndex) => (
              <div
                key={group.title}
                style={{
                  flexShrink: 0,
                  width: '300px',
                  ...(groupIndex > 0
                    ? {
                        paddingLeft: '24px',
                        borderLeft: `1px solid ${dividerColor}`,
                      }
                    : {}),
                }}
              >
                <p
                  style={{
                    margin: 0,
                    font: '600 16px/24px Inter, sans-serif',
                    color: isDark ? ACCENT : '#603db5',
                  }}
                >
                  {group.title}
                </p>
                <p
                  style={{
                    margin: '4px 0 0',
                    font: '400 14px/20px Inter, sans-serif',
                    color: isDark ? TEXT_DIM : '#6b7280',
                  }}
                >
                  {group.description}
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '10px',
                    marginTop: '16px',
                  }}
                >
                  {group.links.map((link) => (
                    <NavLinkItem key={link.label} link={link} theme={theme} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

type Props = { theme?: Theme };

const DesktopHeader: FC<Props> = ({ theme = 'dark' }) => {
  const { show, isOnTop } = useHiddenHeader();
  const { pathname } = useRouter();
  const navigation = useNavigation();
  const [open, setOpen] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMenu = (label: string) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(label);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(null), 150);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    const onDown = (e: MouseEvent) => {
      const header = document.getElementById('desktop-header');
      if (!header?.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  useEffect(() => {
    setOpen(null);
  }, [pathname]);

  const isDark = theme === 'dark';
  const triggerColor = isDark ? '#fff' : '#1a1a1a';
  const activeItem = navigation.find((item) => item.label === open);

  return (
    <header
      id="desktop-header"
      className={classNames(
        'fixed left-0 top-0 z-40 hidden w-full transition-all duration-300 lg:block',
        {
          '-translate-y-full pointer-events-none': !show && !isOnTop,
        }
      )}
      style={{
        padding: '24px 40px',
        // eslint-disable-next-line no-nested-ternary
        backgroundColor: !isOnTop
          ? isDark
            ? 'rgba(2,3,20,0.6)'
            : '#fff'
          : 'transparent',
        backdropFilter: !isOnTop && isDark ? 'blur(16px)' : undefined,
        // eslint-disable-next-line no-nested-ternary
        boxShadow: !isOnTop
          ? isDark
            ? '0 1px 0 rgba(255,255,255,0.06)'
            : '0 1px 0 rgba(0,0,0,0.1)'
          : undefined,
      }}
      onMouseLeave={scheduleClose}
    >
      {/* inner nav row */}
      <div
        style={{
          maxWidth: '1440px',
          marginInline: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '48px',
        }}
      >
        {/* Full page reload is intentional: routes through nginx to the Lovable homepage */}
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
        <a
          href="/"
          aria-label="MindPal – go to homepage"
          className="inline-block transition-transform hover:scale-105"
        >
          <div className="w-[140px]">
            {isDark ? <LogoWhite /> : <LogoPrimary />}
          </div>
        </a>

        <nav style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {navigation.map((item) => {
            const isActive = isItemActive(item, pathname);
            const color = isActive ? ACCENT : triggerColor;

            if (item.href) {
              return (
                <Link
                  key={item.label}
                  href={item.href as unknown as Route}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    borderRadius: '4px',
                    font: '400 16px/24px Inter, sans-serif',
                    color,
                    transition: 'color .2s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = isDark
                      ? APP_LINK
                      : '#603db5';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = color;
                  }}
                >
                  {item.label}
                </Link>
              );
            }

            const isOpen = open === item.label;
            return (
              <button
                key={item.label}
                type="button"
                data-state={isOpen ? 'open' : 'closed'}
                aria-expanded={isOpen}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  borderRadius: '4px',
                  font: '400 16px/24px Inter, sans-serif',
                  // eslint-disable-next-line no-nested-ternary
                  color: isOpen ? (isDark ? APP_LINK : '#603db5') : color,
                  transition: 'color .2s',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => openMenu(item.label)}
                onClick={() => setOpen(isOpen ? null : item.label)}
              >
                {item.label}
                <Icon
                  icon="mdi:chevron-down"
                  aria-hidden="true"
                  style={{
                    width: 16,
                    height: 16,
                    transition: 'transform .2s',
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              </button>
            );
          })}

          <NavigationButton
            href="/?contact=true"
            isInternalLink
            variant="unstyled"
            className="flex items-center gap-2 rounded-lg bg-[#603db5] px-7 font-semibold text-white transition-colors hover:brightness-110"
            style={{
              minHeight: '52px',
              font: '600 16px/20px Inter, sans-serif',
            }}
            suffixIcon="material-symbols:arrow-forward"
          >
            Tell us your bottleneck
          </NavigationButton>
        </nav>
      </div>

      {/* dropdown panel */}
      {activeItem?.rows && (
        <NavPanel
          item={activeItem}
          theme={theme}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />
      )}
    </header>
  );
};

export default DesktopHeader;
